// src/Features/Projects/pages/ProjectEdit/ProjectEdit.tsx
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useProjectData, ProjectEditForm } from "@/features/Projects";

export default function ProjectEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { data: project, isLoading, error } = useProjectData(id || "");

  const handleSuccess = (projectId: string) => {
    // Navegar para a página do projeto editado
    navigate(`/projects/${projectId}`);
  };

  const handleCancel = () => {
    navigate(`/projects/${id}`);
  };

  const handleGoBack = () => {
    navigate("/projects");
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Breadcrumb Skeleton */}
        <div className="flex items-center space-x-4">
          <div className="animate-pulse bg-raro-gray-200 h-6 w-48 rounded"></div>
        </div>

        {/* Form Skeleton */}
        <div className="bg-white rounded-2xl shadow-raro border border-primary-100">
          <div className="px-8 py-6 border-b border-primary-100">
            <div className="flex items-center space-x-3">
              <div className="animate-pulse bg-raro-gray-200 w-12 h-12 rounded-xl"></div>
              <div className="space-y-2">
                <div className="animate-pulse bg-raro-gray-200 h-8 w-48 rounded"></div>
                <div className="animate-pulse bg-raro-gray-200 h-4 w-64 rounded"></div>
              </div>
            </div>
          </div>
          
          <div className="p-8 space-y-6">
            <div className="animate-pulse bg-raro-gray-200 h-12 w-full rounded"></div>
            <div className="animate-pulse bg-raro-gray-200 h-32 w-full rounded"></div>
            <div className="animate-pulse bg-raro-gray-200 h-12 w-full rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !project) {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-4">
          <button
            onClick={handleGoBack}
            className="flex items-center space-x-2 text-raro-gray-600 hover:text-primary-600 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span className="font-medium font-montserrat">
              Voltar aos Projetos
            </span>
          </button>
        </div>

        {/* Error Message */}
        <div className="bg-white rounded-2xl shadow-raro border border-red-200 p-8 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-raro-gray-900 font-montserrat">
                Projeto não encontrado
              </h3>
              <p className="text-raro-gray-600 mt-1">
                O projeto que você está tentando editar não existe ou foi removido.
              </p>
            </div>
            <button
              onClick={handleGoBack}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
            >
              Voltar aos Projetos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Breadcrumb/Navigation */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleGoBack}
          className="flex items-center space-x-2 text-raro-gray-600 hover:text-primary-600 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span className="font-medium font-montserrat">
            Voltar aos Projetos
          </span>
        </button>
        
        <span className="text-raro-gray-400">·</span>
        
        <span className="text-raro-gray-600 font-medium font-montserrat">
          Editando: {project.name}
        </span>
      </div>

      {/* Form */}
      <ProjectEditForm 
        project={project}
        onSuccess={handleSuccess} 
        onCancel={handleCancel} 
      />

      {/* Tips Section */}
      <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-2xl p-8 border border-primary-200">
        <h3 className="text-lg font-semibold text-primary-900 font-montserrat mb-4">
          💡 Dicas para editar seu projeto
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-primary-800 mb-2">
              Melhore sua descrição:
            </h4>
            <ul className="text-sm text-primary-700 space-y-1">
              <li>• Seja mais específico sobre funcionalidades</li>
              <li>• Adicione requisitos técnicos importantes</li>
              <li>• Inclua informações sobre usuários alvo</li>
              <li>• Detalhe o problema que resolve</li>
            </ul>
          </div>

          <div>
            <h4 className="font-medium text-primary-800 mb-2">
              Use a IA para:
            </h4>
            <ul className="text-sm text-primary-700 space-y-1">
              <li>• Expandir descrições muito simples</li>
              <li>• Sugerir funcionalidades adicionais</li>
              <li>• Melhorar a clareza do texto</li>
              <li>• Identificar considerações técnicas</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 p-4 bg-primary-200/50 rounded-lg">
          <p className="text-sm text-primary-800 font-medium">
            🤖 A IA do Gemini pode ajudar a refinar e expandir sua descrição com base nas suas alterações!
          </p>
        </div>
      </div>
    </div>
  );
}